import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { instanceStatic, type InstanceTransform } from './Instancing';
import { ModelLibrary } from './ModelLibrary';

/** Optional per-instance recolour for the deck cannons. */
export interface CannonPalette {
  barrel?: number;
  carriage?: number;
  metal?: number;
}

/**
 * Ship-local deck cannon positions. These mirror the authoritative muzzle
 * offsets in `server/src/systems/Cannons.ts` so the visuals line up with where
 * the server spawns each cannonball. Bow is +X, port is -Z.
 */
export const DECK_CANNON_ANCHORS = {
  port: new THREE.Vector3(0, 2.0, -3.0),
  starboard: new THREE.Vector3(0, 2.0, 3.0),
} as const;

/**
 * GLB cannon normalisation (`kenney-pirate/cannon.glb`): the barrel runs along
 * +Z (muzzle at z ≈ +0.991), the barrel axis sits at y ≈ 0.527 and the wheels
 * touch y = 0. We scale ×1.25 so the barrel spans ~2.4 m and translate so the
 * barrel axis lands on the deck anchor (y = 2.0) and the muzzle reaches the
 * authoritative muzzle offset (z = ±5.2).
 */
const GLB_CANNON_SCALE = 1.25;
const GLB_CANNON_AXIS_Y = 0.527;
const GLB_CANNON_MUZZLE_Z = 0.991;

/** Deck muzzle offsets (world/ship-local), matching `DECK_CANNON_ANCHORS`. */
const CANNON_MUZZLE_Y = 2.0;
const CANNON_MUZZLE_Z = 5.2;

/** True on coarse-pointer/touch or narrow viewports, matching Game's mobile profile. */
function detectMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 760;
}

/** Compose a local part placement (position + optional Euler rotation). */
function partMatrix(position: THREE.Vector3, rotation?: THREE.Euler): THREE.Matrix4 {
  const quaternion = new THREE.Quaternion().setFromEuler(rotation ?? new THREE.Euler());
  return new THREE.Matrix4().compose(position, quaternion, new THREE.Vector3(1, 1, 1));
}

/**
 * Deck cannons for one ship, drawn from the CC0 `cannon` GLB when it loads.
 *
 * The GLB is a single-material, three-mesh prop, so `ModelLibrary` merges it
 * into one geometry and the two broadsides render as a single `InstancedMesh`
 * (one draw call) instead of the three instanced meshes the procedural fallback
 * uses. The public `muzzleAnchors` still expose each barrel's muzzle as an
 * `Object3D` for muzzle-flash or anchor needs, and a custom `CannonPalette`
 * still tints the GLB (a hostile ship keeps its dark recolour).
 */
export class DeckCannons {
  readonly group = new THREE.Group();
  readonly muzzleAnchors: { port: THREE.Object3D; starboard: THREE.Object3D };

  private readonly library: ModelLibrary;
  private readonly ownsLibrary: boolean;
  private readonly palette: CannonPalette;
  private readonly lowDetail: boolean;
  private disposed = false;

  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly instancedMeshes: THREE.InstancedMesh[] = [];

  private glbMesh: THREE.InstancedMesh | null = null;
  private glbGeometry: THREE.BufferGeometry | null = null;
  private glbMaterial: THREE.Material | null = null;

  constructor(palette: CannonPalette = {}, library?: ModelLibrary, lowDetail = detectMobile()) {
    this.palette = palette;
    this.lowDetail = lowDetail;
    this.library = library ?? new ModelLibrary();
    this.ownsLibrary = !library;
    this.group.name = 'deck-cannons';

    // Muzzle anchors keep the previous world-space contract.
    const starboardMuzzle = new THREE.Object3D();
    starboardMuzzle.position.set(0, CANNON_MUZZLE_Y, CANNON_MUZZLE_Z);
    const portMuzzle = new THREE.Object3D();
    portMuzzle.position.set(0, CANNON_MUZZLE_Y, -CANNON_MUZZLE_Z);
    this.group.add(starboardMuzzle, portMuzzle);
    this.muzzleAnchors = { port: portMuzzle, starboard: starboardMuzzle };

    if (this.lowDetail) this.buildProceduralMerged();
    else this.buildProcedural();
    void this.preloadGlb();
  }

  dispose(): void {
    this.disposed = true;
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    for (const instanced of this.instancedMeshes) {
      this.group.remove(instanced);
      instanced.dispose();
    }
    this.geometries.length = 0;
    this.materials.length = 0;
    this.instancedMeshes.length = 0;

    if (this.glbMesh) {
      this.group.remove(this.glbMesh);
      this.glbMesh.dispose();
      this.glbMesh = null;
    }
    if (this.glbGeometry) {
      this.glbGeometry.dispose();
      this.glbGeometry = null;
    }
    if (this.glbMaterial) {
      this.glbMaterial.dispose();
      this.glbMaterial = null;
    }
    if (this.ownsLibrary) this.library.dispose();
  }

  /** Load the GLB once and swap the procedural cannons when it lands. */
  private async preloadGlb(): Promise<void> {
    await this.library.load(['cannon']);
    if (this.disposed || !this.library.has('cannon')) return;
    this.buildGlb();
  }

  /** Replace the procedural instanced cannons with the single GLB instanced mesh. */
  private buildGlb(): void {
    const source = this.library.extractGeometry('cannon');
    if (!source) return;

    const tint = this.paletteTint();
    let material: THREE.Material = source.material;
    if (tint !== null) {
      const clone = (source.material as THREE.MeshStandardMaterial).clone();
      clone.color.setHex(tint);
      material = clone;
      this.glbMaterial = clone;
    }

    const template = new THREE.Mesh(source.geometry, material);
    template.castShadow = true;
    template.receiveShadow = true;
    const mesh = instanceStatic(
      template,
      [glbCannonTransform('starboard'), glbCannonTransform('port')],
      material,
    );

    // Drop the procedural meshes: their geometry/material are caller-owned.
    for (const instanced of this.instancedMeshes) {
      this.group.remove(instanced);
      instanced.dispose();
    }
    this.instancedMeshes.length = 0;
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    this.geometries.length = 0;
    this.materials.length = 0;

    this.glbGeometry = source.geometry;
    this.glbMesh = mesh;
    this.group.add(mesh);
  }

  /** A single representative tint colour for a custom palette, or null. */
  private paletteTint(): number | null {
    const { barrel, carriage, metal } = this.palette;
    if (barrel === undefined && carriage === undefined && metal === undefined) return null;
    return barrel ?? carriage ?? metal ?? null;
  }

  /** Build the procedural broadside cannon fallback. */
  private buildProcedural(): void {
    const barrelMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: this.palette.barrel ?? 0x2b2b2b,
        roughness: 0.45,
        metalness: 0.7,
      }),
    );
    const carriageMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: this.palette.carriage ?? 0x5a3d22,
        roughness: 0.7,
        metalness: 0.05,
      }),
    );
    const metalMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: this.palette.metal ?? 0x1f1f1f,
        roughness: 0.5,
        metalness: 0.6,
      }),
    );

    // Shared part geometries.
    const barrelGeometry = this.geometry(new THREE.CylinderGeometry(0.14, 0.18, 2.4, 10));
    const carriageGeometry = this.geometry(new THREE.BoxGeometry(1.1, 0.45, 0.9));
    const wheelGeometry = this.geometry(new THREE.CylinderGeometry(0.32, 0.32, 0.16, 10));

    // Per-part local placement, before the port cannon's 180° yaw is applied.
    const barrelLocal = {
      position: new THREE.Vector3(0, 0, 1.0),
      rotation: new THREE.Euler(Math.PI / 2, 0, 0),
    };
    const carriageLocal = {
      position: new THREE.Vector3(0, -0.42, 0.4),
    };
    const wheelLocal = {
      position: new THREE.Vector3(0, -0.32, 0.4),
      rotation: new THREE.Euler(0, 0, Math.PI / 2),
    };

    // Barrels: one instance per side.
    this.group.add(
      this.instanced(barrelGeometry, barrelMaterial, [
        cannonTransform('starboard', barrelLocal),
        cannonTransform('port', barrelLocal),
      ]),
    );

    // Carriages: one instance per side.
    this.group.add(
      this.instanced(carriageGeometry, carriageMaterial, [
        cannonTransform('starboard', carriageLocal),
        cannonTransform('port', carriageLocal),
      ]),
    );

    // Wheels: two per carriage (left/right), instanced across both sides.
    const wheelTransforms: InstanceTransform[] = [];
    for (const side of ['starboard', 'port'] as const) {
      for (const xSign of [-1, 1]) {
        wheelTransforms.push(
          cannonTransform(side, {
            position: new THREE.Vector3(xSign * 0.55, wheelLocal.position.y, wheelLocal.position.z),
            rotation: wheelLocal.rotation,
          }),
        );
      }
    }
    this.group.add(this.instanced(wheelGeometry, metalMaterial, wheelTransforms));
  }

  /**
   * Low-detail procedural fallback: bake the barrel, carriage and wheels into
   * one geometry and instance the two broadsides as a single draw call. The
   * GLB swap below still replaces it with the nicer single draw-call cannon.
   */
  private buildProceduralMerged(): void {
    const color = this.palette.barrel ?? this.palette.carriage ?? this.palette.metal ?? 0x2b2b2b;
    const material = this.material(
      new THREE.MeshStandardMaterial({ color, roughness: 0.55, metalness: 0.5 }),
    );

    const barrel = new THREE.CylinderGeometry(0.14, 0.18, 2.4, 10);
    barrel.applyMatrix4(
      partMatrix(new THREE.Vector3(0, 0, 1.0), new THREE.Euler(Math.PI / 2, 0, 0)),
    );
    const carriage = new THREE.BoxGeometry(1.1, 0.45, 0.9);
    carriage.applyMatrix4(partMatrix(new THREE.Vector3(0, -0.42, 0.4)));
    const wheelTemplate = new THREE.CylinderGeometry(0.32, 0.32, 0.16, 10);
    const parts: THREE.BufferGeometry[] = [barrel, carriage];
    for (const xSign of [-1, 1]) {
      const wheel = wheelTemplate.clone();
      wheel.applyMatrix4(
        partMatrix(new THREE.Vector3(xSign * 0.55, -0.32, 0.4), new THREE.Euler(0, 0, Math.PI / 2)),
      );
      parts.push(wheel);
    }
    wheelTemplate.dispose();

    const merged = mergeGeometries(parts, false);
    for (const part of parts) part.dispose();
    if (!merged) {
      // Rare fallback: keep the three-material broadside if the merge fails.
      this.buildProcedural();
      return;
    }

    this.group.add(
      this.instanced(this.geometry(merged), material, [
        cannonTransform('starboard', { position: new THREE.Vector3() }),
        cannonTransform('port', { position: new THREE.Vector3() }),
      ]),
    );
  }

  /** Build one shadow-casting instanced mesh from a shared part geometry. */
  private instanced(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    transforms: InstanceTransform[],
  ): THREE.InstancedMesh {
    const template = new THREE.Mesh(geometry, material);
    template.castShadow = true;
    template.receiveShadow = true;
    const mesh = instanceStatic(template, transforms);
    this.instancedMeshes.push(mesh);
    return mesh;
  }

  private geometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.geometries.push(geometry);
    return geometry;
  }

  private material<T extends THREE.Material>(material: T): T {
    this.materials.push(material);
    return material;
  }
}

/** Combine a cannon part's local placement with a side's yaw + anchor. */
function cannonTransform(
  side: 'starboard' | 'port',
  local: { position: THREE.Vector3; rotation?: THREE.Euler },
): InstanceTransform {
  const groupPosition = side === 'port' ? DECK_CANNON_ANCHORS.port : DECK_CANNON_ANCHORS.starboard;
  const groupYaw = side === 'port' ? Math.PI : 0;

  const position = local.position
    .clone()
    .applyEuler(new THREE.Euler(0, groupYaw, 0))
    .add(groupPosition);

  const rotation = new THREE.Quaternion()
    .setFromEuler(new THREE.Euler(0, groupYaw, 0))
    .multiply(new THREE.Quaternion().setFromEuler(local.rotation ?? new THREE.Euler()));
  const euler = new THREE.Euler().setFromQuaternion(rotation);

  return { position, rotation: euler };
}

/** Build the GLB cannon placement for one broadside (bow +X, port -Z). */
function glbCannonTransform(side: 'starboard' | 'port'): InstanceTransform {
  const yaw = side === 'port' ? Math.PI : 0;
  const muzzleSign = side === 'port' ? -1 : 1;
  return {
    position: new THREE.Vector3(
      0,
      CANNON_MUZZLE_Y - GLB_CANNON_AXIS_Y * GLB_CANNON_SCALE,
      muzzleSign * (CANNON_MUZZLE_Z - GLB_CANNON_MUZZLE_Z * GLB_CANNON_SCALE),
    ),
    rotation: new THREE.Euler(0, yaw, 0),
    scale: new THREE.Vector3(GLB_CANNON_SCALE, GLB_CANNON_SCALE, GLB_CANNON_SCALE),
  };
}
